import { IProduct, IProducts } from 'gcf-components/*';
import { IComponent } from 'gcf-player/shared-utils';
import { compact, get, groupBy, isEmpty } from 'lodash';
import { IGcfComponentConfig } from 'src/interfaces/Component';
import { ComponentTypes } from 'src/interfaces/ComponentTypesList';
import {
  IGcfProductListGroups,
  IGcfProductListProduct,
} from 'src/interfaces/Product';
import { getPlainText } from 'src/utils';
import { cleanQuillEmptyContent, getProductName } from 'src/utils/gcf-helper';
import { lang } from 'src/utils/language';
import { isProductImageVisible } from 'src/utils/product-attributes';

import { IProductLineupAccordionData } from './ProductLineupAccordion.interface';

const getFirstActiveVariationByCreationOrder = (product?: IProduct) =>
  Object.values(product?.variations ?? {})
    .toSorted(
      (a, b) =>
        new Date(a.createdAt ?? '').getTime() -
        new Date(b.createdAt ?? '').getTime(),
    )
    .find((variation) => variation.active);

export const getProductLineupLabelFromProducts = (
  gcfGroupProduct: IGcfProductListProduct,
  product?: IProduct,
) => {
  const productVariation = product?.variations[gcfGroupProduct.productCode];
  const activeGroupProductVariationName =
    productVariation?.active && productVariation.name
      ? productVariation.name
      : undefined;
  const assetName = Object.values(product?.assets ?? {})[0]?.[0];

  const name =
    cleanQuillEmptyContent(gcfGroupProduct.config.nameOverride) || // lineup override
    product?.nameOverride || // product name override
    activeGroupProductVariationName ||
    getFirstActiveVariationByCreationOrder(product)?.name ||
    (assetName ? getProductName(assetName) : '') || // product asset name
    'Product'; //default

  return `${name}`;
};

export const getProductImageStatus = (product: IGcfProductListProduct) => {
  return isProductImageVisible(product);
};

const getProductLineupAccordionItem = (
  groupOrder: number,
  product: IGcfProductListProduct,
  products?: IProducts,
) => ({
  ...product,
  key: product.uid ?? '',
  label: getProductLineupLabelFromProducts(
    product,
    products?.[product.productCode],
  ),
  isImageVisible: getProductImageStatus(product),
});

const mapGroupOrderWithIndex = (productListGroups: IGcfProductListGroups) => {
  const groupedData = Object.values(
    groupBy(Object.values(productListGroups), (col) =>
      get(col, 'config.columnOrder', 1),
    ),
  );

  return new Map(
    groupedData.flatMap((group) =>
      group.map(
        (groupItem, index) => [groupItem.order, index] as [number, number],
      ),
    ),
  );
};

const transformProductListGroupsToAccordions = (
  productListGroups: IGcfProductListGroups,
  products: IProducts,
) => {
  const accordions: IProductLineupAccordionData[] = [];

  // since there can be missing group orders, we need to map group order with index to get the correct index for each column
  const groupOrderIndexMap = mapGroupOrderWithIndex(productListGroups);

  // index starts from 1
  for (const [index, group] of Object.entries(productListGroups)) {
    const groupIndex = Number(index) - 1;

    accordions[groupIndex] = {
      key: String(group.order),
      title:
        group.config.groupHeader?.text ||
        `${lang.category} ${
          (groupOrderIndexMap.get(group.order) as number) + 1
        }`,
      columnOrder: group.config.columnOrder || 1,
      items: [],
    };

    for (const product of Object.values(group.products)) {
      accordions[groupIndex].items.push(
        getProductLineupAccordionItem(group.order, product, products),
      );
    }
  }

  // in case there are missing group orders, we need to filter out the undefined values
  return compact(accordions);
};

export const getProductLineupAccordionsData = (
  component?: IComponent,
  products?: IProducts,
): IProductLineupAccordionData[] => {
  if (!component) {
    return [];
  }

  const productListGroups = (component.config as IGcfComponentConfig)
    ?.productList?.groups;

  if (!productListGroups) {
    return [];
  }

  let productLineupAccordions: IProductLineupAccordionData[] = [];

  switch (component.type) {
    case ComponentTypes.LISTING_WITH_OPTIONAL_VISUALS:
    case ComponentTypes.LISTING_VISUALS_GALLERY: {
      if (isEmpty(products) || !productListGroups) {
        return [];
      }

      productLineupAccordions = transformProductListGroupsToAccordions(
        productListGroups,
        products,
      );
      break;
    }
    case ComponentTypes.SINGLE_SHELF: {
      const componentConfig = component.config as IGcfComponentConfig;
      const [group] = Object.values(productListGroups ?? {});
      const header = componentConfig.header;
      const title =
        header?.visible && cleanQuillEmptyContent(header.text || '')
          ? getPlainText(header.text)
          : lang.products;

      productLineupAccordions.push({
        key: String(group?.order ?? 1),
        title,
        items: [],
      });

      if (group) {
        for (const product of Object.values(group.products)) {
          productLineupAccordions[0].items.push(
            getProductLineupAccordionItem(group.order, product, products),
          );
        }
      }

      break;
    }
    default:
      break;
  }

  return productLineupAccordions;
};

export const getProductListModalFilters = (
  productCategoryIds?: number[],
  productCode?: string,
) => {
  if (!productCategoryIds || !productCategoryIds.length) {
    return productCode ? [`product_code.notlist=${productCode}`] : undefined;
  }

  const filters = [`product_category_id=${productCategoryIds.join('|')}`];

  if (productCode) {
    filters.push(`product_code.notlist=${productCode}`);
  }

  return filters;
};
